import React, { useState, useRef } from "react";
import { FiUploadCloud, FiFile, FiCheck, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import api from "@/services/api";
import toast from "react-hot-toast";

export default function ResumeUpdateCard({ onSuccess }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState(null); // null, 'uploading', 'parsing', 'success', 'error'
  const [fileName, setFileName] = useState(null);
  const [lastFile, setLastFile] = useState(null); // Store file for retry on error
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.includes("pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setFileName(file.name);
    setLastFile(file);
    setIsUploading(true);
    setStatus("uploading");

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);

      // Upload file to S3
      const { ok: uploadOk, data: fileUrl } = await api.post("/file", {
        file: { rawBody: base64, name: file.name },
        folder: "/resumes",
      });

      if (!uploadOk) throw new Error("Failed to upload file");

      setStatus("parsing");

      // Trigger resume parsing
      const { ok, data: updatedUser } = await api.post("/user/enrich-from-resume", {
        resume_url: fileUrl,
      });

      if (!ok) throw new Error("Failed to parse resume");

      setStatus("success");
      toast.success("Profile updated from resume!");

      // Callback to refresh the profile
      if (onSuccess) onSuccess(updatedUser);

      // Reset after a delay
      setTimeout(() => {
        setStatus(null);
        setFileName(null);
        setLastFile(null);
      }, 3000);
    } catch (error) {
      console.error("Error:", error);
      setStatus("error");
      toast.error(error.message || "Failed to update profile");
    } finally {
      setIsUploading(false);
    }
  };

  const retryUpload = () => {
    if (lastFile) {
      handleFile(lastFile);
    } else {
      setStatus(null);
      setFileName(null);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const getStatusContent = () => {
    switch (status) {
      case "uploading":
        return (
          <div className="flex items-center gap-3 text-blue-600">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Uploading {fileName}...</span>
          </div>
        );
      case "parsing":
        return (
          <div className="flex items-center gap-3 text-violet-600">
            <div className="w-5 h-5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Analyzing your resume...</span>
          </div>
        );
      case "success":
        return (
          <div className="flex items-center gap-3 text-emerald-600">
            <FiCheck className="w-5 h-5" />
            <span className="text-sm font-medium">Profile updated successfully!</span>
          </div>
        );
      case "error":
        return (
          <div className="flex flex-col items-center gap-3 text-red-600">
            <div className="flex items-center gap-3">
              <FiAlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Failed to update. Please try again.</span>
            </div>
            <button
              disabled={isUploading}
              onClick={retryUpload}
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded border border-red-300 text-red-700 text-xs font-medium bg-white hover:bg-red-50 transition disabled:opacity-60"
              type="button"
            >
              <FiRefreshCw className="w-4 h-4" /> Try Again
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-violet-50 to-white px-5 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <FiFile className="w-4 h-4 text-violet-600" />
          <h3 className="font-semibold text-gray-900 text-sm">Update from Resume</h3>
        </div>
      </div>

      <div className="p-4">
        {status && status !== "error" ? (
          <div className="py-6 flex justify-center">{getStatusContent()}</div>
        ) : status === "error" ? (
          <div className="py-6 flex justify-center">{getStatusContent()}</div>
        ) : (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
              ${isDragging ? "border-violet-400 bg-violet-50" : "border-gray-200 hover:border-violet-300 hover:bg-gray-50"}
            `}
          >
            <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />

            <FiUploadCloud className={`w-8 h-8 mx-auto mb-3 ${isDragging ? "text-violet-500" : "text-gray-400"}`} />

            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium text-violet-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-400">PDF only, max 10MB</p>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-3 text-center">We'll extract your experiences, skills, education and more</p>
      </div>
    </div>
  );
}
